import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Typography,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from '@mui/material';
import { EmailOutlined as EmailIcon } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { SearchAndFilterBar, SkeletonLoader } from '../../components/Common';
import Paper from '@mui/material/Paper';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import TeacherForm from '../../components/Forms/TeacherForm';
import { teacherService, emailService, subjectService } from '../../services/apiService';
import { formatDate } from '../../utils/dateFormat';
import { showToast } from '../../utils/toast';

const Teachers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);
  const drawerWidth = 280;

  // State management
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Search and filter state - initialize from URL or default to active filter (1 = active, 0 = inactive)
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const statusParam = searchParams.get('status');
    return {
      status: statusParam || '1' // Default to '1' (active) if not in URL
    };
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Server-side infinite scroll state
  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const offsetRef = useRef(0);
  const [isAppending, setIsAppending] = useState(false);

  // Roles are now fetched from backend in TeacherForm component

  // Test email modal state
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testRecipientName, setTestRecipientName] = useState('');
  const [testRecipientEmail, setTestRecipientEmail] = useState('');
  const [testEmailError, setTestEmailError] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Fetch teachers from API
  const fetchTeachers = useCallback(async (search = '', showSearchLoader = false, append = false, customOffset = null) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const sentinelEl = sentinelRef.current; let io = null; if (sentinelEl && sentinelEl._io) { io = sentinelEl._io; try { io.unobserve(sentinelEl); } catch (e) { } }
    try {
      if (showSearchLoader) { setSearchLoading(true); }
      else if (!append) { setLoading(true); } else { setIsAppending(true); }
      setError(null);

      // Build query parameters
      const params = {};
      if (search.trim()) { params.search = search.trim(); }
      params.role_name = 'Teacher';
      // Add is_active filter - always include it, default to '1' (active) if not set
      // Convert to string and ensure it's either '1' or '0'
      const statusValue = filters.status !== undefined && filters.status !== null && filters.status !== '' 
        ? String(filters.status) 
        : '1'; // Default to active
      params.is_active = statusValue === '1' ? '1' : '0';
      const nextOffset = customOffset !== null ? customOffset : (append ? offsetRef.current : 0);
      params.limit = pageSize;
      params.offset = nextOffset;

      const response = await teacherService.getAll(params);

      if (response.success) {
        const fetchedTeachers = response.data || [];
        // Backend already filters by is_active, so no need for client-side filtering
        const merged = append ? [...teachers, ...fetchedTeachers] : fetchedTeachers;
        setTeachers(merged);
        setFilteredTeachers(merged);
        setHasMore((fetchedTeachers.length || 0) === pageSize);
        setOffset(nextOffset + (fetchedTeachers.length || 0));
        offsetRef.current = nextOffset + (fetchedTeachers.length || 0);
      } else {
        setError(response.error || 'Failed to fetch teachers');
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setError(err.message || 'Failed to fetch teachers');
      setHasMore(false);
    } finally {
      // Use setTimeout to batch state updates and prevent focus loss
      setTimeout(() => {
        if (showSearchLoader) { setSearchLoading(false); }
        setLoading(false);
        setIsAppending(false);
        fetchingRef.current = false;
        const el = sentinelRef.current; if (el && el._io) { try { el._io.observe(el); } catch (e) { } }
      }, 0);
    }
  }, [teachers, filters.status]);

  // Update URL when search or filters change (skip on initial load to avoid conflicts)
  useEffect(() => {
    if (isInitialLoad) return; // Skip URL update on initial load
    
    const params = new URLSearchParams();
    if (searchTerm) {
      params.set('search', searchTerm);
    }
    // Always include status filter (default to '1' for active if not set)
    const statusToSet = filters.status || '1';
    params.set('status', statusToSet);
    
    // Only update URL if params have changed to avoid infinite loops
    const currentSearch = searchParams.get('search') || '';
    const currentStatus = searchParams.get('status') || '1';
    if (searchTerm !== currentSearch || statusToSet !== currentStatus) {
      setSearchParams(params, { replace: true });
    }
  }, [searchTerm, filters.status, searchParams, setSearchParams, isInitialLoad]);

  // Initialize URL with default filter on first load if not present
  useEffect(() => {
    if (isInitialLoad) {
      const currentStatus = searchParams.get('status');
      if (!currentStatus) {
        // Set default '1' (active) filter in URL if not present
        const params = new URLSearchParams();
        if (searchTerm) {
          params.set('search', searchTerm);
        }
        params.set('status', '1');
        setSearchParams(params, { replace: true });
      }
    }
  }, [isInitialLoad, searchParams, setSearchParams, searchTerm]);

  // Load teachers on component mount with default filter applied
  useEffect(() => {
    if (isInitialLoad) {
      setOffset(0); offsetRef.current = 0; setHasMore(true);
      // Apply default filter on initial load
      fetchTeachers(searchTerm, false, false, 0);
      setIsInitialLoad(false);
    }
  }, []);

  // Search on typing with debounce
  useEffect(() => {
    if (isInitialLoad) return;
    const timer = setTimeout(() => {
      setOffset(0); offsetRef.current = 0; setHasMore(true);
      fetchTeachers(searchTerm, true, false, 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reload when filters change (backend handles filtering)
  useEffect(() => {
    if (!isInitialLoad) {
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      fetchTeachers(searchTerm, false, false, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  // IntersectionObserver for appending
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (!first?.isIntersecting) return;
      if (hasMore && offsetRef.current > 0 && !fetchingRef.current && !loading && !searchLoading) {
        fetchTeachers(searchTerm, false, true);
      }
    }, { root: null, rootMargin: '300px', threshold: 0 });
    el._io = io;
    io.observe(el);
    return () => { try { io.disconnect(); } catch (e) { } if (el && el._io === io) el._io = null; };
  }, [hasMore, loading, searchLoading, fetchTeachers, searchTerm]);

  // Search and filter handlers - memoized to prevent SearchAndFilterBar re-renders
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleFilter = useCallback((filterValues) => {
    setFilters(filterValues);
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    setFilters({ status: '1' }); // Reset to default '1' (active) filter
  }, []);

  // Filter options for SearchAndFilter component - memoized to prevent SearchAndFilterBar re-renders
  const filterOptions = useMemo(() => [
    {
      field: 'status',
      label: 'Status',
      options: [
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' }
      ]
    }
  ], []);


  // (Removed client-side infinite logic; using server-side paging above)

  const handleAdd = useCallback(() => {
    setEditingTeacher(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((teacher) => {
    setEditingTeacher(teacher);
    setModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(async (teacher) => {
    const isCurrentlyActive = teacher.is_active === '1' || teacher.is_active === 1 || teacher.is_active === true;
    const newActiveStatus = isCurrentlyActive ? 0 : 1;

    // If trying to deactivate, check if teacher is assigned to any subject
    if (newActiveStatus === 0) {
      try {
        const subjectsResponse = await subjectService.getAll({ teacher_id: teacher.id });
        const subjects = subjectsResponse.success ? (subjectsResponse.data || []) : (Array.isArray(subjectsResponse) ? subjectsResponse : []);
        
        if (subjects.length > 0) {
          showToast.error('Cannot deactivate teacher. This teacher is assigned to subjects. Please unassign the teacher from all subjects first.');
          return;
        }
      } catch (err) {
        console.error('Error checking subjects:', err);
        showToast.error('Failed to check subject assignments. Please try again.');
        return;
      }
    }

    // Proceed with activation/deactivation
    try {
      const response = await teacherService.update(teacher.id, { is_active: newActiveStatus });
      
      if (response && !response.success) {
        showToast.error(response.error || 'Failed to update teacher status');
        return;
      }

      showToast.success(newActiveStatus === 1 ? 'Teacher activated successfully' : 'Teacher deactivated successfully');
      
      // Reset pagination and refetch from the beginning with current filters
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      await fetchTeachers(searchTerm, false, false, 0);
    } catch (err) {
      console.error('Error updating teacher status:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update teacher status';
      showToast.error(errorMsg);
    }
  }, [fetchTeachers, searchTerm, filters]);

  const handleView = useCallback((teacher) => {
    navigate(`/users/teachers/${teacher.id}`);
  }, [navigate]);

  // Modal handlers
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingTeacher(null);
  }, []);

  const handleTestEmailClose = useCallback(() => {
    if (sendingTestEmail) return;
    setTestEmailOpen(false);
    setTestEmailError('');
  }, [sendingTestEmail]);

  const handleSendTestEmail = useCallback(async () => {
    const trimmedEmail = testRecipientEmail.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setTestEmailError('Enter a valid email address.');
      return;
    }

    setSendingTestEmail(true);
    setTestEmailError('');
    try {
      const payload = {
        email: trimmedEmail,
        name: testRecipientName.trim() || 'Test Teacher',
        login_email: trimmedEmail,
        password: 'Test@123',
      };
      const response = await emailService.sendTest(payload);
      if (response?.success) {
        showToast.success('Test email sent successfully');
      } else {
        throw new Error(response?.error || 'Failed to send test email');
      }
      setTestEmailOpen(false);
      setTestRecipientEmail('');
      setTestRecipientName('');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to send test email';
      setTestEmailError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setSendingTestEmail(false);
    }
  }, [testRecipientEmail, testRecipientName]);

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      setError(null);

      let response;
      if (editingTeacher) {
        // Update existing teacher
        response = await teacherService.update(editingTeacher.id, formData);
      } else {
        // Create new teacher
        response = await teacherService.create(formData);
      }

      if (response && !response.success) {
        const errorMsg = response.error || 'Failed to save teacher';
        showToast.error(errorMsg);
        // Don't set error state for save errors - keep table visible, error shown via toast

        // Set field-level errors for duplicate email/phone
        const fieldErrors = {};
        if (errorMsg.toLowerCase().includes('email')) {
          fieldErrors.email = errorMsg;
        }
        if (errorMsg.toLowerCase().includes('phone')) {
          fieldErrors.phone = errorMsg;
        }
        setFormErrors(fieldErrors);
        return;
      }

      showToast.success(editingTeacher ? 'Teacher updated successfully' : 'Teacher created successfully');
      setFormErrors({});

      // Reset pagination and refetch from the beginning with current filters
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      // Refresh the teachers list after successful create/update with current search
      await fetchTeachers(searchTerm, false, false, 0);

      // Reset editingTeacher to null to clear the form after successful creation
      // This ensures the form is reset when the modal opens again
      const wasCreating = !editingTeacher;
      setEditingTeacher(null);
      
      // Close modal
      handleModalClose();
    } catch (err) {
      console.error('Error saving teacher:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save teacher';
      showToast.error(errorMsg);
      // Don't set error state for save errors - keep table visible, error shown via toast

      // Set field-level errors for duplicate email/phone
      const fieldErrors = {};
      if (errorMsg.toLowerCase().includes('email')) {
        fieldErrors.email = errorMsg;
      }
      if (errorMsg.toLowerCase().includes('phone')) {
        fieldErrors.phone = errorMsg;
      }
      setFormErrors(fieldErrors);
    } finally {
      setModalLoading(false);
    }
  }, [editingTeacher, fetchTeachers, searchTerm]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ mt: 2 }}>
                <SkeletonLoader variant="list" count={5} />
              </Box>
            ) : (
              <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="h6"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                        letterSpacing: '0.01em',
                      }}
                    >
                      Teachers Management
                    </Typography>
                    <Chip
                      label={`Filter: ${(filters.status || '1') === '1' || filters.status === 1 ? 'Active' : 'Inactive'}`}
                      color={(filters.status || '1') === '1' || filters.status === 1 ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                    {searchTerm && (
                      <Chip
                        label={`Search: "${searchTerm}"`}
                        color="primary"
                        size="small"
                        variant="outlined"
                        onDelete={() => setSearchTerm('')}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                  </Stack>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => {
                      setTestEmailOpen(true);
                      setTestRecipientName('');
                      setTestRecipientEmail('');
                      setTestEmailError('');
                    }}
                    size="small"
                  >
                    Send Test Email
                  </Button>
                </Stack>
                <SearchAndFilterBar
                  title=""
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  onCreate={handleAdd}
                  onClear={handleClear}
                  searchPlaceholder="Search by name..."
                  filterOptions={filterOptions}
                  createButtonText="Add Teacher"
                  searchValue={searchTerm}
                  filterValue={filters}
                />

                <Box sx={{ mt: 2 }}>
                  {loading && !searchLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                      <CircularProgress />
                    </Box>
                  ) : error && !searchLoading ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  ) : (
                    <>
                      {filteredTeachers.length === 0 ? (
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 4, 
                            borderRadius: 3, 
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 'calc(100vh - 300px)',
                            width: '100%'
                          }}
                        >
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            No teachers found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Try adjusting your search or filters to view the list
                          </Typography>
                        </Paper>
                      ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: { xs: 1.5, sm: 2 } }}>
                          {filteredTeachers.map((row) => {
                        const isActive = row.is_active === '1' || row.is_active === 1 || row.is_active === true;
                        return (
                          <Paper key={row.id} variant="outlined" sx={{ p: { xs: 2, sm: 2.4 }, borderRadius: 2, borderColor: 'divider', position: 'relative' }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                <Avatar 
                                  src={row.profile_image || null}
                                  alt={row.full_name} 
                                  sx={{ width: 48, height: 48, bgcolor: getAvatarColor(row.full_name), color: '#ffffff', fontWeight: 700 }}
                                >
                                  {!row.profile_image && getInitials(row.full_name)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle1" fontWeight={700}>{row.full_name}</Typography>
                                    <Chip size="small" label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'success' : 'error'} variant="outlined" />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Email:</strong> {row.email || '—'} &nbsp;·&nbsp;
                                    <strong>Phone:</strong> {row.phone || '—'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Joined:</strong> {formatDate(row.date_of_joining)}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '100px' }, flexWrap: 'wrap', rowGap: 1 }}>
                                <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleView(row)}>View</Button>
                                <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(row)}>Edit</Button>
                                {isActive ? (
                                  <Button 
                                    size="small" 
                                    color="error" 
                                    variant="outlined"
                                    onClick={() => handleToggleActive(row)}
                                  >
                                    Deactivate
                                  </Button>
                                ) : (
                                  <Button 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                    onClick={() => handleToggleActive(row)}
                                  >
                                    Activate
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                          })}
                          <Box ref={sentinelRef} sx={{ height: 8 }} />
                          {isAppending && (
                            <Box display="flex" justifyContent="center" p={2}>
                              <CircularProgress size={20} />
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Teacher Form Modal */}
      < TeacherForm
        open={modalOpen}
        onClose={() => {
          handleModalClose();
          setFormErrors({});
        }}
        initialData={editingTeacher}
        onSubmit={handleModalSave}
        loading={modalLoading}
        title={editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}
        externalErrors={formErrors}
      />

      {/* Test email dialog */}
      < Dialog
        open={testEmailOpen}
        onClose={handleTestEmailClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send Test Teacher Email</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            label="Recipient Name"
            fullWidth
            value={testRecipientName}
            onChange={(e) => setTestRecipientName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Recipient Email"
            fullWidth
            value={testRecipientEmail}
            onChange={(e) => {
              setTestRecipientEmail(e.target.value);
              if (testEmailError) {
                setTestEmailError('');
              }
            }}
            error={Boolean(testEmailError)}
            helperText={testEmailError || 'The teacher welcome template will be sent using your SMTP settings.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestEmailClose} disabled={sendingTestEmail}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTestEmail}
            disabled={sendingTestEmail}
            startIcon={sendingTestEmail ? <CircularProgress size={18} color="inherit" /> : <EmailIcon fontSize="small" />}
          >
            {sendingTestEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
};

export default Teachers;